import { Component } from "react";
import FileSaver from "file-saver";
import XLSX from "xlsx";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu } from "antd";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

const ARABIC_CHAR_REGEX = /[\u0600-\u06FF]/;

export const splitBilingualHeader = (
  header: string,
): { english: string; arabic: string } => {
  const slashParts = header.split(" / ");
  if (slashParts.length === 2) {
    return { english: slashParts[0].trim(), arabic: slashParts[1].trim() };
  }

  const openParen = header.indexOf("(");
  const closeParen = header.lastIndexOf(")");
  if (openParen > 0 && closeParen > openParen) {
    const english = header.slice(0, openParen).trim();
    const arabic = header.slice(openParen + 1, closeParen).trim();
    if (ARABIC_CHAR_REGEX.test(arabic)) {
      return { english, arabic };
    }
  }

  return { english: header, arabic: "" };
};

/** Bilingual header in one cell: English / Arabic */
export const formatBilingualHeader = (header: string): string => {
  const { english, arabic } = splitBilingualHeader(header);
  return arabic ? `${english} / ${arabic}` : english;
};

const getCellDisplayLength = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  return String(value).length;
};

const buildAutoFitColumnWidths = (
  headers: string[],
  rows: { [key: string]: unknown }[],
): { wch: number }[] =>
  headers.map((header) => {
    const displayHeader = formatBilingualHeader(header);
    let maxLen = Math.max(displayHeader.length, 8);

    rows.forEach((row) => {
      maxLen = Math.max(maxLen, getCellDisplayLength(row[header]));
    });

    return { wch: Math.min(maxLen + 1, 55) };
  });
interface Props {
  apiData?: any;
  fileName: string;
  otherSheets?: any[];
  buttonName?: string,
  buttonColor?: string,
  multipleFiles?: boolean,
  generateData?: any,
  numberFormatKeys?: string[];
  formatBilingualHeaders?: boolean;
}

export const exportToCSV = async (
  apiData: { Amount: string }[],
  fileName: string,
  generateData?: any,
  otherSheets?: any[],
  additionalNumberFormatKeys?: string[],
  formatBilingualHeaders = false,
) => {
  if (generateData) {
    apiData = await generateData()
  }

  const allFiles = [];
  const numbersFormatKeys = [
    ...(additionalNumberFormatKeys ?? []),
    "Due this Month",
    "Due principal this Month",
    "Due fees this Month",
    "User Collections",
    "Previous Dues After Collections",
    "Total due this Month",
    "Total Outstanding",
    "Outstanding Amount",
    "Outstanding Principal",
    "Outstanding Fees",
    "outstandingAmount",
    "remainingInstallments",
  ];
  const numbersToStrings = [
    //fix numbers to strings
    "User ID",
    "Staff ID",
    "National ID / رقم الهويه",
    "Bank Account Number",
    "userID",
    "Remaining Payment Cycle",
    "installmentID",
    "paymentCycle",
    "AdvanceID",
    "nationalID",
    "numberOfMonths",
    "serviceTypeId",
    "companyID"
  ]
  if (Array.isArray(apiData) && apiData.length > 0) allFiles.push({
    name: fileName,
    data: apiData,
  })

  if (otherSheets) {
    for (let index = 0; index < otherSheets.length; index++) {
      if (otherSheets[index].onClick) {
        otherSheets[index].data = await otherSheets[index].onClick()
      }
      allFiles.push(otherSheets[index]);
    }
  }
  for (let i = 0; i < allFiles.length; i++) {
    const sheetData = allFiles[i]?.data ?? [];
    const headers = Object.keys(sheetData[0] || {});
    const worksheet = XLSX.utils.json_to_sheet(sheetData);

    const numberFormatIndices = numbersFormatKeys
      .map(key => headers.indexOf(key))
      .filter(index => index !== -1);

    const stringFormatIndices = numbersToStrings
      .map(key => headers.indexOf(key))
      .filter(index => index !== -1);

    Object.keys(worksheet).forEach(cellRef => {
      if (cellRef[0] === '!') return;

      const cell = worksheet[cellRef];
      const value = cell.v;

      const { r, c } = XLSX.utils.decode_cell(cellRef);

      if (r === 0) {
        if (formatBilingualHeaders) {
          cell.t = "s";
          cell.v = formatBilingualHeader(String(value ?? ""));
        }
        return;
      }

      if (stringFormatIndices.includes(c)) {
        cell.t = "s";     // set type to string
        cell.v = String(value ?? "");
        cell.z = undefined; // remove formatting
        return;
      }

      if (numberFormatIndices.includes(c)) {
        const num = Number(value);
        if (value !== 'N/A' && value !== '' && value !== null && value !== undefined && !Number.isNaN(num)) {
          cell.t = 'n';
          cell.v = num;
          cell.z = "#,##0.00";
        } else {
          cell.t = 's';
          cell.v = value === null || value === undefined || value === '' ? 'N/A' : String(value);
          cell.z = undefined;
        }
        return;
      }

      if (!isNaN(value) && value !== "" && value !== null && cell.t === 'n') {
        const num = Number(value);
        if (!Number.isNaN(num)) {
          cell.t = 'n';
          cell.v = num;
          cell.z = "0";
        }
      }
    });

    worksheet["!cols"] = buildAutoFitColumnWidths(headers, sheetData);

    const textFormatKeys = [
      "REFERENCE",
      "DEBIT_ACCOUNT",
      "BENEFICIARY_ZIP_CODE",
      "BENEFICIARY_BANK_ZIP_CODE",
      "TRANSACTION_AMOUNT",
      "TRANSACTION_DATE",
      "BENEFICIARY_CORP_ID_CODE",
      "BENEFICIARY_THIRD_PARTY_TYPE",
      "BENEFICIARY_THIRD_PARTY_VALUE",
    ];

    if (allFiles[i]?.data?.length > 0) {
      const headers = Object.keys(allFiles[i]?.data[0]);

      const textFormatIndices = textFormatKeys.map(key => headers.indexOf(key)).filter(index => index !== -1);

      if (textFormatIndices.length > 0) {
        for (let rowIndex = 0; rowIndex < allFiles[i]?.data?.length; rowIndex++) {
          textFormatIndices.forEach(colIndex => {
            const colLetter = XLSX.utils.encode_col(colIndex);
            const cellRef = colLetter + (rowIndex + 2); // +2 because row 1 is headers

            if (worksheet[cellRef]) {
              worksheet[cellRef].t = 's'; // 's' means string/text format

              if (!worksheet[cellRef].z) {
                worksheet[cellRef].z = '@'; // '@' is Excel's text format
              }
            }
          });
        }
      }
    }

    let sheets: { [key: string]: XLSX.WorkSheet } = {
      data: worksheet,
    };

    const wb = { Sheets: sheets, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    allFiles.length > 1 ?
      FileSaver.saveAs(data, allFiles[i].statusName + fileExtension) :
      FileSaver.saveAs(data, allFiles[i].name + fileExtension)
  }
};

class ExportToExcel extends Component<Props> {
  state = {};
  render() {
    const menu = (
      <Menu>
        <Menu.Item
          icon={<DownloadOutlined />}
          onClick={(e) => exportToCSV(
            this.props.apiData,
            this.props.fileName,
            this.props.generateData,
            this.props.otherSheets,
            this.props.numberFormatKeys,
            this.props.formatBilingualHeaders,
          )}
        >
          Download all sheets
        </Menu.Item>
        {this.props.apiData && <Menu.Item
          icon={<DownloadOutlined />}
          onClick={(e) => exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData, this.props.otherSheets, this.props.numberFormatKeys, this.props.formatBilingualHeaders)}
        >
          {this.props.fileName}
        </Menu.Item>}
        {this.props.otherSheets?.map((sheet, i) => (
          <Menu.Item
            key={i}
            icon={<DownloadOutlined />}
            onClick={async (e) => {
              if (sheet.onClick) {
                const data = await sheet.onClick()
                sheet.data = data
              }
              exportToCSV(sheet.data, sheet.statusName, this.props.generateData, undefined, this.props.numberFormatKeys, this.props.formatBilingualHeaders)
            }}
          >
            {sheet.name}
          </Menu.Item>
        ))}
      </Menu>
    );
    return (
      <>
        {!this.props.otherSheets ? (

          <>
            {this.props.multipleFiles ?
              <Button type="default" onClick={() => exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData, undefined, this.props.numberFormatKeys, this.props.formatBilingualHeaders)}
                style={{ color: this.props.buttonColor, borderColor: this.props.buttonColor, margin: '0 19px' }}>
                {this.props.buttonName}
              </Button>
              : <DownloadOutlined
                onClick={(e) =>
                  exportToCSV(this.props.apiData, this.props.fileName, this.props.generateData, undefined, this.props.numberFormatKeys, this.props.formatBilingualHeaders)
                }
                style={{ float: "right", margin: "20px 0 0 0", fontSize: "25px" }}
              />}
          </>
        ) : (
          <Dropdown overlay={menu}>
            <DownloadOutlined style={{ float: "right", margin: "20px 0 0 0", fontSize: "25px" }} />
          </Dropdown>
        )}

      </>
    );
  }
}

export default ExportToExcel;